/**
 * Tiny Markdown-ish renderer for static legal documents (commitment agreement,
 * privacy policy, etc.). Intentionally lightweight — we don't need a full
 * Markdown parser for these short texts, and pulling in `react-markdown` (with
 * its `unified` / `remark` deps) is more than the use-case warrants.
 *
 * Supported syntax:
 *   - `# heading` → <h2>
 *   - `## heading` → <h3>
 *   - `- item` → bulleted list (consecutive `- ` lines collapse into a single <ul>)
 *   - blank line → paragraph break
 *   - `**bold**` and `_italic_` inline (single-pass replace)
 *   - everything else → <p>
 *
 * If a future document needs richer Markdown (tables, code blocks, links),
 * lift to `react-markdown` then.
 */

import { Fragment, type JSX } from 'react';

export interface LegalTextProps {
  text: string;
  className?: string;
}

export function LegalText({ text, className }: LegalTextProps): JSX.Element {
  const blocks = parseBlocks(text);
  return (
    <div className={className}>
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`;
        switch (block.kind) {
          case 'h1':
            return (
              <h2 key={key} className="font-display text-display-md text-ink mt-s5 mb-s3 first:mt-0">
                {renderInline(block.text)}
              </h2>
            );
          case 'h2':
            return (
              <h3 key={key} className="font-display text-display-sm text-ink mt-s4 mb-s2">
                {renderInline(block.text)}
              </h3>
            );
          case 'ul':
            return (
              <ul key={key} className="list-disc pl-s5 my-s3 space-y-s1 text-gray-2">
                {block.items.map((item, idx) => (
                  <li key={idx}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case 'p':
            return (
              <p key={key} className="my-s3 text-gray-2 leading-relaxed">
                {renderInline(block.text)}
              </p>
            );
        }
      })}
    </div>
  );
}

type Block =
  | { kind: 'h1'; text: string }
  | { kind: 'h2'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'p'; text: string };

function parseBlocks(input: string): Block[] {
  const lines = input.split('\n');
  const blocks: Block[] = [];
  let buffer: string[] = [];
  let listBuffer: string[] = [];

  const flushParagraph = (): void => {
    if (buffer.length === 0) return;
    blocks.push({ kind: 'p', text: buffer.join(' ').trim() });
    buffer = [];
  };
  const flushList = (): void => {
    if (listBuffer.length === 0) return;
    blocks.push({ kind: 'ul', items: listBuffer });
    listBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'h1', text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'h2', text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      listBuffer.push(line.slice(2).trim());
      continue;
    }
    flushList();
    buffer.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

function renderInline(text: string): JSX.Element {
  // Single-pass replace for **bold** and _italic_. We split on the markers and
  // rebuild as React fragments so no `dangerouslySetInnerHTML` is needed.
  const parts: { text: string; bold: boolean; italic: boolean }[] = [];
  let remaining = text;
  while (remaining.length > 0) {
    const boldMatch = /\*\*(.+?)\*\*/.exec(remaining);
    const italicMatch = /(^|[^_])_([^_]+?)_(?!_)/.exec(remaining);

    interface Match {
      start: number;
      end: number;
      text: string;
      bold: boolean;
      italic: boolean;
    }
    const candidates: Match[] = [];
    if (boldMatch) {
      candidates.push({
        start: boldMatch.index,
        end: boldMatch.index + boldMatch[0].length,
        text: boldMatch[1] ?? '',
        bold: true,
        italic: false,
      });
    }
    if (italicMatch) {
      const italicStart = italicMatch.index + (italicMatch[1] ? italicMatch[1].length : 0);
      candidates.push({
        start: italicStart,
        end: italicStart + 1 + (italicMatch[2]?.length ?? 0) + 1,
        text: italicMatch[2] ?? '',
        bold: false,
        italic: true,
      });
    }

    if (candidates.length === 0) {
      parts.push({ text: remaining, bold: false, italic: false });
      break;
    }
    candidates.sort((a, b) => a.start - b.start);
    const first = candidates[0];
    if (!first) break;
    if (first.start > 0) {
      parts.push({ text: remaining.slice(0, first.start), bold: false, italic: false });
    }
    parts.push({ text: first.text, bold: first.bold, italic: first.italic });
    remaining = remaining.slice(first.end);
  }
  return (
    <Fragment>
      {parts.map((part, i) => {
        if (part.bold) {
          return (
            <strong key={i} className="text-ink font-medium">
              {part.text}
            </strong>
          );
        }
        if (part.italic) {
          return <em key={i}>{part.text}</em>;
        }
        return <Fragment key={i}>{part.text}</Fragment>;
      })}
    </Fragment>
  );
}
